// Tremor Button [v1.0.0]

import React from "react"
import { Slot } from "@radix-ui/react-slot"
import { RiLoader2Fill } from "@remixicon/react"
import { tv, type VariantProps } from "tailwind-variants"

import { cx } from "../../utils/cx"
import { focusRing } from "../../utils/focusRing"

// Anthropic-console pill button language:
//   - rounded-full (pill) for confident, friendly affordance
//   - generous horizontal padding (px-4) so the label has air
//   - no borders on primary/destructive (solid fill speaks for itself);
//     secondary uses a soft dark fill in dark mode + neutral in light
//   - ghost stays text-only with a subtle hover wash
//   - light keeps a low-key dark-gray pill (like screenshot's "Activity")
const buttonVariants = tv({
  base: [
    "relative inline-flex items-center justify-center whitespace-nowrap rounded-full border-0 px-4 py-1.5 text-center text-sm font-medium transition-colors duration-100 ease-in-out",
    "disabled:pointer-events-none",
    focusRing,
  ],
  variants: {
    variant: {
      primary: [
        "text-gray-950",
        "bg-white",
        "hover:bg-stone-200",
        "dark:bg-white dark:text-gray-950 dark:hover:bg-stone-200",
        "disabled:bg-stone-200 disabled:text-gray-400",
        "dark:disabled:bg-white/20 dark:disabled:text-gray-600",
      ],
      secondary: [
        "text-gray-900 dark:text-gray-50",
        "bg-stone-100 hover:bg-stone-200",
        "dark:bg-white/[0.08] dark:hover:bg-white/[0.12]",
        "disabled:text-gray-400 dark:disabled:text-gray-600",
      ],
      light: [
        "text-gray-700 dark:text-gray-200",
        "bg-stone-100 hover:bg-stone-200",
        "dark:bg-white/[0.05] dark:hover:bg-white/[0.08]",
        "disabled:bg-stone-100 disabled:text-gray-400",
        "dark:disabled:bg-white/[0.04] dark:disabled:text-gray-600",
      ],
      ghost: [
        "text-gray-700 dark:text-gray-300",
        "bg-transparent hover:bg-stone-100",
        "dark:hover:bg-white/[0.06]",
        "disabled:text-gray-400 dark:disabled:text-gray-600",
      ],
      destructive: [
        "text-white",
        "bg-red-600 hover:bg-red-700",
        "dark:bg-red-500 dark:hover:bg-red-600",
        "disabled:bg-red-300 disabled:text-white",
        "dark:disabled:bg-red-900 dark:disabled:text-red-400",
      ],
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

interface ButtonProps
  extends React.ComponentPropsWithoutRef<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  loadingText?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild,
      isLoading = false,
      loadingText,
      className,
      disabled,
      variant,
      children,
      ...props
    }: ButtonProps,
    forwardedRef,
  ) => {
    const Component = asChild ? Slot : "button"
    return (
      <Component
        ref={forwardedRef}
        className={cx(buttonVariants({ variant }), className)}
        disabled={disabled || isLoading}
        tremor-id="tremor-raw"
        {...props}
      >
        {isLoading ? (
          <span className="pointer-events-none flex shrink-0 items-center justify-center gap-1.5">
            <RiLoader2Fill
              className="size-4 shrink-0 animate-spin"
              aria-hidden="true"
            />
            <span className="sr-only">
              {loadingText ? loadingText : "Loading"}
            </span>
            {loadingText ? loadingText : children}
          </span>
        ) : (
          children
        )}
      </Component>
    )
  },
)

Button.displayName = "Button"

export { Button, buttonVariants, type ButtonProps }
